<template>
  <div class="relative inline-block text-left">
    <!-- Toggle button -->
    <button
      type="button"
      class="flex items-center p-1 rounded-md text-primary-font text-sm transition select-none cursor-pointer"
      @click.stop="toggleDropdown"
    >
      <span :class="colorflagName()">{{ currentLocale?.name }}</span>
      <img v-if="currentLocale" :src="currentLocale.pic" class="w-5 h-5 object-cover ml-1" />
    </button>

    <!-- Dropdown -->
    <div
      v-if="open"
      :class="[
        'absolute right-0 w-42 origin-top-right bg-white border border-primary-font/30 rounded-lg shadow-xl overflow-hidden z-50',
        direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
      ]"
    >
      <ul class="text-sm text-black border-t-0 max-h-64 overflow-y-auto">
        <li
          v-for="(locale, index) in localeList"
          :key="index"
          class="flex items-center px-2 py-2 hover:bg-primary cursor-pointer"
          @click="selectLocale(locale)"
        >
          <img :src="locale.pic" class="w-5 h-5 object-cover" />
          <span class="ml-2">{{ locale.name }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import thai from '@/assets/images/thailand.png';
import usa from '@/assets/images/usa.png';
import ar from '@/assets/images/ar.png';
import cn from '@/assets/images/cn.png';
import de from '@/assets/images/de.png';
import es from '@/assets/images/es.png';
import hi from '@/assets/images/hi.png';
import id from '@/assets/images/id.png';
import jp from '@/assets/images/jp.png';
import ko from '@/assets/images/ko.png';
import my from '@/assets/images/my.png';
import ph from '@/assets/images/ph.png';
import pt from '@/assets/images/pt.png';
import rs from '@/assets/images/rs.png';
import vn from '@/assets/images/vn.png';
import zh from '@/assets/images/zh.png';
import tr from '@/assets/images/tr.png';

type LocaleItem = { name: string; value: string; pic: string };

export default defineComponent({
  name: 'LanguageButton',
  props: {
    // 'down' opens the dropdown below the button (default, used in the top navbar)
    // 'up' opens the dropdown above the button (used inside the mobile sidebar,
    // where the button sits near the bottom of the screen and opening downward
    // would push the list off-screen)
    direction: {
      type: String as () => 'up' | 'down',
      default: 'down'
    }
  },
  data() {
    return {
      localeList: [
        { name: 'ไทย', value: 'th', pic: thai },
        { name: 'English', value: 'en', pic: usa },
        { name: '中文', value: 'zh', pic: zh },
        { name: 'Tiếng Việt', value: 'vn', pic: vn },
        { name: 'Русский', value: 'rs', pic: rs },
        { name: 'Português', value: 'pt', pic: pt },
        { name: 'Filipino', value: 'ph', pic: ph },
        { name: 'Arabic', value: 'ar', pic: ar },
        { name: '中文', value: 'cn', pic: cn },
        { name: 'Deutsch', value: 'de', pic: de },
        { name: 'Español', value: 'es', pic: es },
        { name: 'हिन्दी', value: 'hi', pic: hi },
        { name: 'Bahasa Indonesia', value: 'id', pic: id },
        { name: '日本語', value: 'jp', pic: jp },
        { name: '한국어', value: 'ko', pic: ko },
        { name: 'မြန်မာ', value: 'my', pic: my },
        { name: 'Türkçe', value: 'tr', pic: tr }
      ] as LocaleItem[],
      currentLocale: null as LocaleItem | null,
      open: false
    };
  },
  created() {
    const raw = localStorage.getItem('language');
    let saved: Partial<LocaleItem> | null = null;
    try {
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    const fallback = this.localeList[1]; // 'en'
    const picked = (saved && saved.value && this.localeList.find(l => l.value === saved!.value)) || fallback;

    this.currentLocale = picked;
    // @ts-ignore
    if (this.$i18n) this.$i18n.locale = picked.value;
  },
  mounted() {
    // close when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      this.open = !this.open;
    },

    selectLocale(locale: LocaleItem) {
      this.handleLocale(locale.value);
      this.open = false;
    },

    handleLocale(value: string) {
      // @ts-ignore
      if (this.$i18n) this.$i18n.locale = value;
      this.setCurrentLocale();
    },

    setCurrentLocale() {
      // @ts-ignore
      const code = this.$i18n ? this.$i18n.locale : 'en';
      const picked = this.localeList.find(item => item.value === code) || null;
      this.currentLocale = picked;
      if (picked) localStorage.setItem('language', JSON.stringify(picked));
    },

    handleClickOutside(event: MouseEvent) {
      const root = this.$el as HTMLElement | null;
      const target = event.target as Node | null;
      if (!root || !target) return;

      if (this.open && !root.contains(target)) {
        this.open = false;
      }
    },

    colorflagName(): string {
      const isLogin = window.location.pathname === '/login';
      const base = 'flex items-center text-sm font-bold';

      const color = isLogin ? 'text-[var(--secondaryFont)]' : 'text-[var(--primaryFont)]';

      return `${base} ${color}`;
    }
  }
});
</script>
